import { useState, useEffect } from "react";
import { createUseStyles } from "react-jss";

import widgets from "styles/widgets";
import { actionService } from "services";
import { ActionCommon, ActionDto, ActionRequestDto } from "dtos/shard/action.dto";
import { useResources } from "hooks/resources.hook";
import { useEditorActions } from "hooks/editor-actions.hook";
import { useEditorPreview } from "hooks/editor-preview.hook";
import EditorControls from "../editor-controls";
import { UserDto } from "dtos/user.dto";

const styles = createUseStyles({
    container: {
        width: "100%",
    },
});

export default function ActionEditor(props: {
    user?: UserDto, // Optional dto of the user.
    action?: Partial<ActionDto>,
    preview?: Partial<ActionDto>,
    onPreviewUpdate?: (previewDto: Partial<ActionDto>) => void,
    onClose?: () => void,
    onSave?:(action: ActionDto) => void,
}): JSX.Element
{
    // Dtos:
    const freshCommon: ActionCommon = {
        name: props.action?.name ?? "",
        power: props.action?.power ?? 1,
    };
    const freshRequestDto: ActionRequestDto = {
        id: props.action?.id,
        ...freshCommon,
        elementId: props.action?.element?.id ?? "",
        actionTypeId: props.action?.actionType?.id ?? "",
        actionEffectId: props.action?.actionEffect?.id ?? "",
    };
    const freshPreviewDto: Partial<ActionDto> = props.preview ? structuredClone(props.preview) : props.action ? structuredClone(props.action) : {
        id: undefined,
        ...freshCommon,
    };

    // Hooks:
    const resources = useResources(props.user);
    const [previewDto, requestDto, previewUpdate, setRequestDto] = useEditorPreview(freshPreviewDto, freshRequestDto, props.onPreviewUpdate);
    const [onTextChange, onNumberChange, onRteChange, onCheckboxChange, onPopup] = useEditorActions(props.user, "action", resources, requestDto, previewDto, setRequestDto, props.onPreviewUpdate);

    // Effects:
    useEffect(() => {
        setRequestDto(freshRequestDto);
    }, [props.action]);

    // Styles:
    const widgetClasses = widgets();
    const classes = styles();

    // Actions:
    const reset = () => {
        setRequestDto(freshRequestDto);
        if (props.onPreviewUpdate) {
            props.onPreviewUpdate(props.action ?? previewDto);
        }
    };
    
    // Return Component:
    return (
        <div className={`${widgetClasses.form} ${classes.container}`}>
            <div className={`${widgetClasses.row}`}>
                <label>Name</label>
                <input id="name" type="text" value={requestDto.name ?? ""} onChange={onTextChange} />
            </div>
            <div className={`${widgetClasses.row}`}>
                <label>Power</label>
                <input id="power" type="number" value={requestDto.power ?? 0} onChange={onNumberChange} />
            </div>
            <div className={`${widgetClasses.row}`}>
                <button id="element" onClick={() => onPopup("element", "elementId", true)}>Element: {previewDto.element?.name ?? "Please Select"}</button>
            </div>
            <div className={`${widgetClasses.row}`}>
                <button id="actionType" onClick={() => onPopup("actionType", "actionTypeId", true)}>Type: {previewDto.actionType?.name ?? "Please Select"}</button>
                <button id="actionEffect" onClick={() => onPopup("actionEffect", "actionEffectId", false)}>Effect: {previewDto.actionEffect?.name ?? "None"}</button>
            </div>

            <hr/>
            <EditorControls
                editorName="action"
                service={actionService}
                subject={props.action}
                subjectName={props.action?.name ?? ""}
                requestDto={requestDto}
                onSave={props.onSave}
                onReset={reset}
                onClose={props.onClose}
            />
            <hr/>
        </div>
    );
}